<template>
  <div class="hyrox-admin-shell py-8 px-4">
    <v-container class="admin-container">
      <!-- Header -->
      <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-black white--text italic-title">HYROX SCORE</h1>
          <h2 class="subtitle-1 yellow--text text--darken-2 font-weight-bold text-uppercase">Admin-Dashboard</h2>
        </div>
        <div class="d-flex gap-3 mt-4 mt-sm-0">
          <v-btn color="#fbc02d" class="black--text font-weight-bold" @click="exportToExcel" :loading="exporting">
            <v-icon left>mdi-microsoft-excel</v-icon> Excel Export
          </v-btn>
          <v-btn text outlined color="grey lighten-1" @click="fetchData">
            <v-icon left>mdi-refresh</v-icon> Aktualisieren
          </v-btn>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Aktive Teams (Teilnehmer)</div>
            <div class="text-h4 font-weight-black white--text my-1">
              {{ stats.activeTeams }} <span class="text-subtitle-1 grey--text font-weight-medium">({{ stats.activeParticipants }} Personen)</span>
            </div>
            <v-progress-linear :value="(stats.activeParticipants / stats.limit) * 100" color="#fbc02d" class="mt-2 rounded"></v-progress-linear>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Teilnehmer-Limit</div>
            <div class="text-h4 font-weight-black white--text my-1">{{ stats.limit }} Plätze</div>
            
            <div class="d-flex align-center mt-2">
              <span class="caption grey--text mr-2">Limit umschalten:</span>
              <v-btn-toggle v-model="limitToggleIndex" mandatory dense class="limit-toggle rounded-lg">
                <v-btn x-small value="84" class="px-2" @click="changeLimit(84)">84</v-btn>
                <v-btn x-small value="126" class="px-2" @click="changeLimit(126)">126</v-btn>
              </v-btn-toggle>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Einnahmen (Bezahlt)</div>
            <div class="text-h4 font-weight-black green--text text--accent-3 my-1">CHF {{ stats.totalPaidRevenue.toFixed(2) }}</div>
            <div class="caption grey--text mt-2">Bestätigte Zahlungen</div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Freie Plätze</div>
            <div class="text-h4 font-weight-black yellow--text text--darken-2 my-1">
              {{ Math.max(0, stats.limit - stats.activeParticipants) }}
            </div>
            <div class="caption grey--text mt-2">von max. {{ stats.limit }} Plätzen</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Registrations Table -->
      <v-card dark class="table-card rounded-xl pa-4">
        <!-- Search and Filters -->
        <v-row class="mb-4 align-center" dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Suchen..."
              single-line
              hide-details
              outlined
              dense
              class="search-field rounded-lg"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="8" class="text-sm-right mt-3 mt-sm-0">
            <v-chip-group v-model="filterStatus" mandatory active-class="active-chip">
              <v-chip filter value="all" small outlined>Alle ({{ registrations.length }})</v-chip>
              <v-chip filter value="pending" small color="orange" outlined>Zahlung offen ({{ countStatus('pending_approval') }})</v-chip>
              <v-chip filter value="paid" small color="green" outlined>Bezahlt ({{ countStatus('paid') }})</v-chip>
              <v-chip filter value="unpaid" small color="red" outlined>Unbezahlt ({{ countStatus('unpaid') }})</v-chip>
              <v-chip filter value="cancelled" small color="grey" outlined>Storniert ({{ countCancelled() }})</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="filteredRegistrations"
          :search="search"
          :loading="loading"
          class="custom-table"
          :items-per-page="15"
          dark>
          
          <!-- Date Format -->
          <template v-slot:item.registeredAt="{ item }">
            <span class="caption">{{ formatDate(item.registeredAt) }}</span>
          </template>

          <!-- Main User Column -->
          <template v-slot:item.user="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold white--text">
                {{ item.user ? `${item.user.vorname} ${item.user.nachname}` : 'Gelöschter User' }}
              </div>
              <div class="caption grey--text">{{ item.user ? item.user.email : '' }}</div>
              <v-chip x-small v-if="userHasInstructorTag(item)" color="green" class="black--text font-weight-black mt-1">INSTRUCTOR</v-chip>
            </div>
          </template>

          <!-- Partner Column -->
          <template v-slot:item.partner="{ item }">
            <div v-if="item.partner && item.partner.email" class="py-1">
              <div class="body-2 font-weight-bold white--text">
                {{ item.partner.firstName }} {{ item.partner.lastName }}
              </div>
              <div class="caption grey--text">{{ item.partner.email }}</div>
              <div class="d-flex gap-1 mt-1">
                <v-chip x-small outlined color="grey lighten-1">MEMBER</v-chip>
                <v-chip x-small v-if="item.partner.isInstructor" color="green" class="black--text font-weight-black">INSTRUCTOR</v-chip>
              </div>
            </div>
            <div v-else class="py-1">
              <v-chip x-small outlined color="grey">Einzelstarter</v-chip>
            </div>
          </template>

          <!-- Price Column -->
          <template v-slot:item.totalFee="{ item }">
            <span class="font-weight-bold" :class="item.totalFee === 0 ? 'green--text' : 'white--text'">
              CHF {{ item.totalFee.toFixed(2) }}
            </span>
          </template>

          <!-- Payment Status Column -->
          <template v-slot:item.paymentStatus="{ item }">
            <v-chip
              x-small
              :color="getPaymentColor(item.paymentStatus)"
              class="font-weight-bold black--text"
              label>
              {{ getPaymentText(item.paymentStatus) }}
            </v-chip>
          </template>

          <!-- Registration Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              :color="item.status === 'active' ? 'green' : 'grey'"
              outlined
              class="font-weight-bold">
              {{ item.status === 'active' ? 'Aktiv' : 'Storniert' }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-menu offset-y dark>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense bg-color="#1c1c1e">
                <!-- Payment updates -->
                <v-list-item @click="updateStatus(item._id, { paymentStatus: 'paid' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="green" small>mdi-cash-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Als bezahlt markieren</v-list-item-title>
                </v-list-item>
                
                <v-list-item @click="updateStatus(item._id, { paymentStatus: 'unpaid' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="red" small>mdi-cash-remove</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Als unbezahlt markieren</v-list-item-title>
                </v-list-item>

                <v-divider class="grey darken-3"></v-divider>

                <!-- Registration status updates -->
                <v-list-item @click="updateStatus(item._id, { status: item.status === 'active' ? 'cancelled' : 'active' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="orange" small>
                      {{ item.status === 'active' ? 'mdi-close-circle' : 'mdi-checkbox-marked-circle' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ item.status === 'active' ? 'Anmeldung stornieren' : 'Anmeldung reaktivieren' }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "HyroxScoreAdmin",
  data() {
    return {
      loading: true,
      exporting: false,
      search: "",
      filterStatus: "all",
      limitToggleIndex: "84",
      
      registrations: [],
      stats: {
        activeParticipants: 0,
        activeTeams: 0,
        totalPaidRevenue: 0,
        limit: 84
      },

      headers: [
        { text: "Datum", value: "registeredAt", width: "90px" },
        { text: "Teilnehmer 1 (Anmelder)", value: "user", sortable: false },
        { text: "Teilnehmer 2 (Partner)", value: "partner", sortable: false },
        { text: "Gebühr", value: "totalFee", align: "right", width: "100px" },
        { text: "Zahlung", value: "paymentStatus", align: "center", width: "110px" },
        { text: "Status", value: "status", align: "center", width: "100px" },
        { text: "", value: "actions", sortable: false, align: "right", width: "60px" }
      ]
    }
  },
  computed: {
    filteredRegistrations() {
      if (this.filterStatus === "all") return this.registrations
      if (this.filterStatus === "cancelled") return this.registrations.filter((r) => r.status === "cancelled")
      
      // Filter by paymentStatus for active registrations
      const active = this.registrations.filter((r) => r.status === "active")
      if (this.filterStatus === "pending") return active.filter((r) => r.paymentStatus === "pending_approval")
      if (this.filterStatus === "paid") return active.filter((r) => r.paymentStatus === "paid")
      if (this.filterStatus === "unpaid") return active.filter((r) => r.paymentStatus === "unpaid")
      
      return this.registrations
    }
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      })
    },
    userHasInstructorTag(item) {
      // If price indicates an instructor fee or is 0, let's mark it.
      // We can also check if the user is linked and has the instructor flag computed.
      return item.totalFee === 0 || (item.totalFee === 20 && item.partner.isInstructor === false)
    },
    getPaymentColor(status) {
      if (status === "paid") return "green accent-3"
      if (status === "pending_approval") return "orange"
      return "red lighten-1"
    },
    getPaymentText(status) {
      if (status === "paid") return "Bezahlt"
      if (status === "pending_approval") return "Freigabe"
      return "Offen"
    },
    countStatus(paymentStatus) {
      return this.registrations.filter((r) => r.status === "active" && r.paymentStatus === paymentStatus).length
    },
    countCancelled() {
      return this.registrations.filter((r) => r.status === "cancelled").length
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await api.hyroxScore.admin.fetchRegistrations()
        this.registrations = res.registrations || []
        this.stats = res.stats || {
          activeParticipants: 0,
          activeTeams: 0,
          totalPaidRevenue: 0,
          limit: 84
        }
        this.limitToggleIndex = String(this.stats.limit)
      } catch (e) {
        this.$toast.error("Registrierungen konnten nicht geladen werden.")
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async changeLimit(limit) {
      try {
        await api.hyroxScore.admin.toggleLimit(limit)
        this.$toast.success(`Teilnehmerlimit auf ${limit} geändert.`)
        await this.fetchData()
      } catch (e) {
        this.$toast.error("Änderung des Limits fehlgeschlagen.")
      }
    },
    async updateStatus(registrationId, updatePayload) {
      try {
        await api.hyroxScore.admin.updateStatus(registrationId, updatePayload)
        this.$toast.success("Status erfolgreich aktualisiert.")
        await this.fetchData()
      } catch (e) {
        this.$toast.error("Statusaktualisierung fehlgeschlagen.")
      }
    },
    exportToExcel() {
      this.exporting = true
      try {
        // Point browser directly to export URL download path
        const downloadUrl = api.hyroxScore.admin.exportCsvUrl()
        
        // Custom header authorization config download helper or direct anchor download
        const token = localStorage.getItem("token")
        
        // Since download requires auth header, we can fetch it via axios blob, or use standard download helper
        // Let's create an anchor with token query fallback if browser supports it, or download via axios:
        const axios = require("axios")
        axios({
          url: downloadUrl,
          method: "GET",
          headers: { Authorization: token },
          responseType: "blob"
        }).then((response) => {
          const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;\uFEFF" })
          const link = document.createElement("a")
          link.href = URL.createObjectURL(blob)
          link.setAttribute("download", "hyrox_score_registrations.csv")
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }).catch(() => {
          this.$toast.error("Export fehlgeschlagen.")
        }).finally(() => {
          this.exporting = false
        })
      } catch (error) {
        this.exporting = false
        this.$toast.error("Export fehlgeschlagen.")
      }
    }
  }
}
</script>

<style scoped>
.hyrox-admin-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

/* Cards */
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

.limit-toggle {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.limit-toggle .v-btn {
  color: #777777 !important;
  background-color: transparent !important;
}

.limit-toggle .v-btn--active {
  color: #000000 !important;
  background-color: #fbc02d !important;
}

/* Table Card */
.table-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.active-chip {
  background-color: rgba(251, 192, 45, 0.1) !important;
  color: #fbc02d !important;
  border-color: #fbc02d !important;
}

/* Custom Table Styles */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: bold !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  color: #777777 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.gap-1 {
  gap: 4px;
}
.gap-3 {
  gap: 12px;
}
</style>
